import { useQuery } from "@tanstack/react-query";
import { get } from "@/api/util";
import DefaultDropdownItem from "@/components/dropdown/DefaultDropdownItem";
import SuperDropdown from "@/components/dropdown/SuperDropdown";
import DefaultLoadingError from "@/components/error/DefaultLoadingError";
import Spinner from "@/components/spinner/Spinner";
import type { TildelbarBrugerDTO } from "@/interfaces/admin";
import {
  getAvatarColorsFromName,
  getInitialsFromName,
} from "../pages/efteruddannelse/utils/displayNameAvatar";

interface AutoCompleteBrugerProps {
  clearAfterSelection?: boolean;
  placeholder?: string;
  onSelectedItemChange: (bruger: TildelbarBrugerDTO) => void;
}

const BrugerAvatar = ({ name }: { name: string }) => {
  const initials = getInitialsFromName(name);
  const { backgroundColor, color } = getAvatarColorsFromName(name);

  return (
    <span
      className="c-avatar c-avatar--round c-avatar--sm flex-shrink-0"
      style={{ backgroundColor, color }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
};

/** Medarbejdere, en henvendelse kan tildeles til.
 *
 * Kilden er `brugere/tildelbare` og ikke den fulde `brugere`-liste: sidstnævnte kræver
 * AdminRead, som en tilsynsmedarbejder ikke har, så opslaget fejlede for alle andre end
 * en Administrator. Endpointet udelader også brugere uden e-mail, fordi CLE-API'et
 * identificerer den tildelte netop på e-mail.
 */
export default function AutoCompleteBruger({
  clearAfterSelection = true,
  placeholder = "Søg på medarbejder ...",
  onSelectedItemChange,
}: AutoCompleteBrugerProps) {
  const { data, isLoading, error } = useQuery<TildelbarBrugerDTO[]>({
    queryKey: ["brugere", "tildelbare"],
    queryFn: () => get({ query: "brugere/tildelbare" }),
  });

  if (isLoading) {
    return (
      <Spinner
        inline
        size={14}
        className="form-control justify-content-start"
        label="Henter medarbejdere ..."
      />
    );
  }

  if (error || !data) {
    return (
      <DefaultLoadingError
        label="Kunne ikke hente medarbejdere"
        refreshLabel="Indlæs igen"
      />
    );
  }

  const sortedBrugere = [...data].sort((a, b) => a.navn.localeCompare(b.navn));

  return (
    <SuperDropdown
      items={sortedBrugere}
      itemsIdKey="id"
      itemToString={(item) => item?.navn ?? ""}
      filterKeys={["navn", "email", "initialer"]}
      clearInputAfterSelection={clearAfterSelection}
      placeholder={placeholder}
      onSelectedItemEvent={(event) => {
        if (event.selectedItem) {
          onSelectedItemChange(event.selectedItem);
        }
      }}
      itemToElement={(bruger) => (
        <DefaultDropdownItem
          label={bruger.navn}
          subLabel={bruger.email}
          leftIcon={<BrugerAvatar name={bruger.navn} />}
        />
      )}
    />
  );
}
